import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Check, X, DollarSign } from "lucide-react";
import { EXPENSE_CATEGORIES, expenseCategoryLabel } from "@costadorada/shared";
import { api } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/cn";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { QueryError } from "@/components/QueryError";
import { AdminRecordActions } from "@/components/AdminRecordActions";

interface Expense {
  id: string;
  category: string;
  description: string;
  amount: string;
  vendor?: string | null;
  receiptUrl?: string | null;
  status: "PENDING" | "APPROVED" | "PAID";
  createdById?: string | null;
  budgetCategoryId?: string | null;
  createdAt: string;
}

interface BudgetCategoryOption {
  id: string;
  categoryName: string;
}

interface BudgetOption {
  id: string;
  status: "DRAFT" | "APPROVED";
  fiscalYear: string;
  categories: BudgetCategoryOption[];
}

const statusVariant: Record<string, "default" | "info" | "warning" | "success"> = {
  PENDING: "warning",
  APPROVED: "info",
  PAID: "success",
};

export function ExpensesPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const { data: expenses = [], isLoading, isError } = useQuery({
    queryKey: ["expenses"],
    queryFn: async () => (await api.get<Expense[]>("/expenses")).data,
  });

  const reviewMutation = useMutation({
    mutationFn: async ({ id, decision }: { id: string; decision: "APPROVED" | "PAID" | "REJECTED" }) =>
      (await api.post(`/expenses/${id}/review`, { decision })).data,
    onSuccess: () => {
      setReviewError(null);
      qc.invalidateQueries({ queryKey: ["expenses"] });
    },
    onError: (err: any) => {
      setReviewError(err?.response?.data?.message ?? t("common.error"));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/expenses/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["expenses"] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("finance.expenses")}</h1>
        <button onClick={() => setOpen(true)} className="btn-primary">
          <Plus size={16} /> {t("finance.newExpense")}
        </button>
      </div>

      {reviewError && (
        <div className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2">
          {reviewError}
        </div>
      )}

      <div className="card p-0 overflow-x-auto">
        {isError ? (
          <div className="p-6"><QueryError /></div>
        ) : isLoading ? (
          <div className="p-6 text-slate-500">{t("common.loading")}</div>
        ) : expenses.length === 0 ? (
          <div className="p-6 text-slate-500">—</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">{t("finance.category")}</th>
                <th className="text-left px-4 py-2">{t("finance.description")}</th>
                <th className="text-left px-4 py-2">{t("finance.vendor")}</th>
                <th className="text-right px-4 py-2">{t("finance.amount")}</th>
                <th className="text-left px-4 py-2">{t("finance.status")}</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {expenses.map((e) => {
                const isSelfReview = !!e.createdById && e.createdById === user?.id && user?.role !== "ADMIN";
                return (
                <tr key={e.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium">
                    {expenseCategoryLabel(e.category, i18n.language.startsWith("es") ? "es" : "en")}
                  </td>
                  <td className="px-4 py-2">
                    {e.description}
                    <div className="text-xs text-slate-500">{formatDate(e.createdAt, i18n.language)}</div>
                  </td>
                  <td className="px-4 py-2">{e.vendor ?? "—"}</td>
                  <td className="px-4 py-2 text-right font-medium">{formatCurrency(e.amount)}</td>
                  <td className="px-4 py-2"><Badge variant={statusVariant[e.status]}>{e.status}</Badge></td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex gap-1 justify-end">
                      {e.status === "PENDING" && (
                        <>
                          <button
                            className="btn bg-green-50 text-green-700 hover:bg-green-100 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                            disabled={isSelfReview}
                            title={isSelfReview ? t("finance.selfApprovalBlocked") : undefined}
                            onClick={() => reviewMutation.mutate({ id: e.id, decision: "APPROVED" })}
                          >
                            <Check size={12} /> {t("finance.approve")}
                          </button>
                          <button
                            className="btn bg-red-50 text-red-700 hover:bg-red-100 text-xs"
                            onClick={() => reviewMutation.mutate({ id: e.id, decision: "REJECTED" })}
                          >
                            <X size={12} /> {t("finance.reject")}
                          </button>
                        </>
                      )}
                      {e.status === "APPROVED" && (
                        <button
                          className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                          disabled={isSelfReview}
                          title={isSelfReview ? t("finance.selfApprovalBlocked") : undefined}
                          onClick={() => reviewMutation.mutate({ id: e.id, decision: "PAID" })}
                        >
                          <DollarSign size={12} /> {t("finance.markPaidExpense")}
                        </button>
                      )}
                      <AdminRecordActions
                        compact
                        entityLabel={e.description}
                        onEdit={() => setEditing(e)}
                        onDelete={() => deleteMutation.mutate(e.id)}
                        disabled={deleteMutation.isPending}
                      />
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {(open || editing) && (
        <ExpenseModal
          key={editing?.id ?? "new"}
          editing={editing}
          onClose={() => {
            setOpen(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function ExpenseModal({ editing, onClose }: { editing: Expense | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const isEdit = !!editing;
  const lang = i18n.language.startsWith("es") ? "es" : "en";
  const [form, setForm] = useState({
    category: editing?.category ?? "",
    description: editing?.description ?? "",
    amount: editing != null ? String(editing.amount) : "",
    vendor: editing?.vendor ?? "",
    receiptUrl: editing?.receiptUrl ?? "",
    budgetCategoryId: editing?.budgetCategoryId ?? "",
  });

  const { data: budgets = [] } = useQuery({
    queryKey: ["budgets"],
    queryFn: async () => (await api.get<BudgetOption[]>("/budgets")).data,
  });
  const approvedBudget = budgets.find((b) => b.status === "APPROVED");

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        category: form.category,
        description: form.description,
        amount: parseFloat(form.amount),
        vendor: form.vendor || null,
        receiptUrl: form.receiptUrl || null,
        budgetCategoryId: form.budgetCategoryId || null,
      };
      return isEdit
        ? (await api.put(`/expenses/${editing!.id}`, payload)).data
        : (await api.post("/expenses", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["budgets"] });
      onClose();
    },
  });

  return (
    <Modal open onClose={onClose} title={isEdit ? t("common.edit") : t("finance.newExpense")}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <div>
          <label className="label">{t("finance.category")}</label>
          <select className="input" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option value="" disabled>
              —
            </option>
            {EXPENSE_CATEGORIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} · {c[lang]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">{t("finance.description")}</label>
          <input className="input" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("finance.amount")} (USD)</label>
            <input type="number" step="0.01" className="input" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("finance.vendor")}</label>
            <input className="input" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="label">URL Recibo</label>
          <input className="input" placeholder="https://..." value={form.receiptUrl} onChange={(e) => setForm({ ...form, receiptUrl: e.target.value })} />
        </div>
        <div>
          <label className="label">{t("finance.budgetCategory")}</label>
          <select
            className="input"
            value={form.budgetCategoryId}
            onChange={(e) => setForm({ ...form, budgetCategoryId: e.target.value })}
          >
            <option value="">{t("finance.unbudgeted")}</option>
            {approvedBudget?.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.categoryName}
              </option>
            ))}
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}
